import { useState, useEffect } from "react";
import FaiBox from "../../../MuiComponents/FaiBox";
import FaiTypography from "../../../MuiComponents/FaiTypography";
import theme from "../../../assets/theme";
import { Form, Formik, FormikProps } from "formik";
import { Grid } from "@mui/material";
import FormField from "../../../components/FormField";
import FaiButton from "../../../MuiComponents/FaiButton";
import FaiSelect from "../../../MuiComponents/FaiSelect";
import { merchantAuthController } from "../../../controllers/authController";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import commonController from "../../../controllers/commonController";
import { useProfile } from "../../../context/ProfileContext";
import { toast } from "react-toastify";

interface InitialObject {
	companyName: string;
	companyEmail: string;
	companyAddress: string;
	gstNumber: string;
	documents: File[];
	state: string;
	country: string;
	city: string;
	pinCode: string;
	isDraft: boolean;
}

interface CountryOption {
	value: string;
	label: string;
	id: number;
}

interface StateOption {
	value: string;
	label: string;
	id: number;
}

interface CityOption {
	value: string;
	label: string;
	id: number;
}

// Validation schema
const BusinessDetailsSchema = Yup.object().shape({
	companyName: Yup.string().required("Company name is required"),
	companyEmail: Yup.string()
		.email("Invalid email")
		.required("Company email is required"),
	gstNumber: Yup.string().required("GST number is required"),
	companyAddress: Yup.string().required("Company address is required"),
	state: Yup.string().required("State is required"),
	country: Yup.string().required("Country is required"),
	city: Yup.string().required("City is required"),
	pinCode: Yup.string().required("Pin code is required"),
});

function BusinessDetails() {
	const { typography, palette, borders } = theme;
	const { profile, setProfile } = useProfile();

	const [initialObject, setInitialObject] = useState<InitialObject>({
		companyName: profile?.businessInformation?.companyName || "",
		companyEmail: profile?.businessInformation?.companyEmail || "",
		gstNumber: profile?.businessInformation?.gstNumber || "",
		documents: profile?.businessInformation?.documents || [],
		companyAddress: profile?.businessInformation?.companyAddress || "",
		state: profile?.businessInformation?.state || "",
		city: profile?.businessInformation?.city || "",
		country: profile?.businessInformation?.country || "",
		pinCode: profile?.businessInformation?.pinCode || "",
		isDraft: false,
	});

	const [countries, setCountries] = useState<CountryOption[]>([]);
	const [states, setStates] = useState<StateOption[]>([]);
	const [cities, setCities] = useState<CityOption[]>([]);
	const [loading, setLoading] = useState(true);
	const navigate = useNavigate();

	useEffect(() => {
		const getCountries = async () => {
			try {
				setLoading(true);
				const response = await commonController.getCountry();

				// Format countries for dropdown
				const countryOptions = response.data?.data?.data?.map(
					(country: any) => ({
						value: country.name,
						label: country.name,
						id: country.id,
					})
				);

				setCountries(countryOptions);
			} catch (error) {
				console.error("Error fetching countries:", error);
			} finally {
				setLoading(false);
			}
		};

		getCountries();
	}, []);

	const getStates = async (countryId: number) => {
		try {
			setLoading(true);
			const response = await commonController.getState(countryId);

			const stateOptions = response.data.data.data.map((state: any) => ({
				value: state.name,
				label: state.name,
				id: state.id,
			}));

			setStates(stateOptions);
			setCities([]); // Reset cities when country changes
		} catch (error) {
			console.error("Error fetching states:", error);
		} finally {
			setLoading(false);
		}
	};

	const getCities = async (stateId: number) => {
		try {
			setLoading(true);
			const response = await commonController.getCity(stateId);

			const cityOptions = response.data.data.data.map((city: any) => ({
				value: city.name,
				label: city.name,
				id: city.id,
			}));

			setCities(cityOptions);
		} catch (error) {
			console.error("Error fetching cities:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (
		values: InitialObject,
		{ setSubmitting }: any
	) => {
		try {
			const formData = new FormData();

			// Append all form fields to FormData
			formData.append("companyName", values.companyName);
			formData.append("companyEmail", values.companyEmail);
			formData.append("gstNumber", values.gstNumber);
			formData.append("companyAddress", values.companyAddress);
			formData.append("state", values.state);
			formData.append("country", values.country);
			formData.append("city", values.city);
			formData.append("pinCode", values.pinCode);
			formData.append("isDraft", "false");

			// Append all uploaded documents
			categoryImages.forEach((doc, index) => {
				if (doc instanceof File) {
					formData.append(`documents[${index}]`, doc);
				}
			});

			const response = await merchantAuthController.setBusinessInfo(formData);

			if (response.status === 200 || response.status === 201) {
				// Update the profile context with the new business information
				setProfile({
					...profile,
					businessInformation: {
						companyName: values.companyName,
						companyEmail: values.companyEmail,
						gstNumber: values.gstNumber,
						companyAddress: values.companyAddress,
						state: values.state,
						country: values.country,
						city: values.city,
						pinCode: values.pinCode,
						documents: values.documents,
					},
				});

				// Handle success - you can add a success toast/notification here
				toast.success("Business details updated successfully");
				if (!profile.businessInformation) {
					// Navigate to next step or dashboard
					navigate("/app/onboarding/bankdetails");
				}
			}
		} catch (error) {
			console.error("Error updating business details:", error);
			// Handle error - you can add an error toast/notification here
		} finally {
			setSubmitting(false);
		}
	};

	const [categoryImages, setCategoryImages] = useState<(File | string)[]>([]);

	// Handle removing a document
	const handleRemoveDocument = (index: number) => {
		setCategoryImages((prev) => prev.filter((_, i) => i !== index));
	};

	return (
		<>
			<FaiBox>
				<FaiBox
					bgColor={palette.white.main}
					className={"business-details"}
					mt={"15px"}
					p={"15px"}
					borderRadius={borders.borderRadius.md}
				>
					<FaiBox>
						<FaiTypography
							variant="h4"
							fontWeight={typography.fontWeightMedium}
						>
							Business Details
						</FaiTypography>
						<FaiTypography variant="h6" mb={"21px"}>
							Please provide your business details to complete your profile and
							start selling on our platform.
						</FaiTypography>
					</FaiBox>

					<Formik
						initialValues={initialObject}
						enableReinitialize={true}
						validateOnBlur={true}
						validateOnChange={true}
						validationSchema={BusinessDetailsSchema}
						onSubmit={handleSubmit}
					>
						{(props: FormikProps<InitialObject>) => {
							const {
								handleSubmit,
								setFieldValue,
								handleChange,
								handleBlur,
								values,
								touched,
								isValid,
								errors,
							} = props;
							return (
								<Form>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Company Name"}
													name={"companyName"}
													placeholder={"Enter Company Name"}
													value={values.companyName}
													onChange={handleChange}
													onBlur={handleBlur}
													error={
														touched.companyName && Boolean(errors.companyName)
													}
													helperText={touched.companyName && errors.companyName}
												/>
											</FaiBox>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Company E-mail"}
													name={"companyEmail"}
													placeholder={"Enter email"}
													value={values.companyEmail}
													onChange={handleChange}
													onBlur={handleBlur}
													error={
														touched.companyEmail && Boolean(errors.companyEmail)
													}
													helperText={
														touched.companyEmail && errors.companyEmail
													}
												/>
											</FaiBox>
										</Grid>
									</Grid>

									<Grid item sm={12} xs={12}>
										<FaiBox mb={1}>
											<FormField
												multiline
												rows={5}
												label={"Company address"}
												name={"companyAddress"}
												placeholder={"Enter Company Address"}
												value={values.companyAddress}
												onChange={handleChange}
												onBlur={handleBlur}
												error={
													touched.companyAddress &&
													Boolean(errors.companyAddress)
												}
												helperText={
													touched.companyAddress && errors.companyAddress
												}
											/>
										</FaiBox>
									</Grid>

									<Grid container spacing={2}>
										<Grid item xs={12} sm={6}>
											<FaiBox
												mb={1}
												ml={0.5}
												lineHeight={0}
												display="inline-block"
											>
												<FaiTypography
													component="label"
													variant="caption"
													fontWeight="bold"
													textTransform="capitalize"
												>
													Country
												</FaiTypography>
											</FaiBox>
											<FaiSelect
												placeholder={"Select Country"}
												name={"country"}
												value={
													values.country
														? {
															value: values.country,
															label: values.country,
														}
														: null
												}
												onChange={(selectedOption: any) => {
													setFieldValue(
														"country",
														selectedOption ? selectedOption.value : ""
													);
													setFieldValue("state", "");
													setFieldValue("city", "");
													if (selectedOption) {
														const country = countries.find(
															(c) => c.value === selectedOption.value
														);
														if (country) {
															getStates(country.id);
														}
													}
												}}
												options={countries}
												error={touched.country && Boolean(errors.country)}
											/>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox
												mb={1}
												ml={0.5}
												lineHeight={0}
												display="inline-block"
											>
												<FaiTypography
													component="label"
													variant="caption"
													fontWeight="bold"
													textTransform="capitalize"
												>
													State
												</FaiTypography>
											</FaiBox>
											<FaiSelect
												placeholder={"Choose State"}
												name={"state"}
												value={
													values.state
														? {
															value: values.state,
															label: values.state,
														}
														: null
												}
												onChange={(selectedOption: any) => {
													setFieldValue(
														"state",
														selectedOption ? selectedOption.value : ""
													);
													setFieldValue("city", "");
													if (selectedOption) {
														const state = states.find(
															(s) => s.value === selectedOption.value
														);
														if (state) {
															getCities(state.id);
														}
													}
												}}
												options={states}
												error={touched.state && Boolean(errors.state)}
											/>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox
												mb={1}
												ml={0.5}
												lineHeight={0}
												display="inline-block"
											>
												<FaiTypography
													component="label"
													variant="caption"
													fontWeight="bold"
													textTransform="capitalize"
												>
													City
												</FaiTypography>
											</FaiBox>
											<FaiSelect
												placeholder={"Select City"}
												name={"city"}
												value={
													values.city
														? {
															value: values.city,
															label: values.city,
														}
														: null
												}
												onChange={(selectedOption: any) => {
													setFieldValue(
														"city",
														selectedOption ? selectedOption.value : ""
													);
												}}
												options={cities}
												error={touched.city && Boolean(errors.city)}
											/>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Pin Code"}
													name={"pinCode"}
													placeholder={"Enter Pin Code"}
													value={values.pinCode}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.pinCode && Boolean(errors.pinCode)}
													helperText={touched.pinCode && errors.pinCode}
												/>
											</FaiBox>
										</Grid>

										<Grid item sm={12} xs={12}>
											<FaiBox mb={1}>
												<FaiBox
													mb={1}
													ml={0.5}
													lineHeight={0}
													display="inline-block"
												>
													<FaiBox>
														<FaiTypography
															component="label"
															variant="caption"
															fontWeight="bold"
															textTransform="capitalize"
														>
															Upload Documents (Multiple allowed)
														</FaiTypography>
													</FaiBox>
												</FaiBox>

												{/* Show existing documents from profile */}
												{profile?.businessInformation?.documents &&
													profile.businessInformation.documents.length > 0 && (
														<FaiBox mb={2}>
															<FaiTypography variant="caption" fontWeight="bold" mb={1}>
																Existing Documents:
															</FaiTypography>
															<FaiBox display="flex" flexWrap="wrap" gap={2} mt={1}>
																{profile.businessInformation.documents.map(
																	(docUrl: string, index: number) => {
																		const isImage = /\.(jpg|jpeg|png|svg|gif)$/i.test(docUrl);
																		const fileName = docUrl.split("/").pop() || `Document ${index + 1}`;
																		return (
																			<FaiBox
																				key={index}
																				position="relative"
																				border="1px solid #eee"
																				borderRadius={2}
																				p={1}
																				sx={{ minWidth: 120 }}
																			>
																				{isImage ? (
																					<img
																						src={docUrl}
																						alt={`Document ${index + 1}`}
																						style={{
																							maxWidth: 100,
																							maxHeight: 100,
																							objectFit: "contain",
																						}}
																					/>
																				) : (
																					<FaiBox
																						display="flex"
																						flexDirection="column"
																						alignItems="center"
																						p={2}
																					>
																						<FaiTypography variant="h5">📄</FaiTypography>
																						<FaiTypography variant="caption" sx={{ wordBreak: "break-all" }}>
																							{fileName.length > 15 ? fileName.slice(0, 15) + "..." : fileName}
																						</FaiTypography>
																					</FaiBox>
																				)}
																			</FaiBox>
																		);
																	}
																)}
															</FaiBox>
														</FaiBox>
													)}

												{/* Show newly uploaded documents */}
												{categoryImages.length > 0 && (
													<FaiBox mb={2}>
														<FaiTypography variant="caption" fontWeight="bold" mb={1}>
															New Documents to Upload ({categoryImages.length}):
														</FaiTypography>
														<FaiBox display="flex" flexWrap="wrap" gap={2} mt={1}>
															{categoryImages.map((doc, index) => {
																const fileName = doc instanceof File ? doc.name : String(doc);
																const isImage = doc instanceof File && doc.type.startsWith("image/");
																return (
																	<FaiBox
																		key={index}
																		position="relative"
																		border="1px solid #1976d2"
																		borderRadius={2}
																		p={1}
																		sx={{ minWidth: 120, backgroundColor: "rgba(25, 118, 210, 0.04)" }}
																	>
																		<div
																			style={{
																				position: "absolute",
																				top: "-10px",
																				right: "-10px",
																				cursor: "pointer",
																				background: "#d32f2f",
																				color: "white",
																				borderRadius: "50%",
																				width: "24px",
																				height: "24px",
																				display: "flex",
																				justifyContent: "center",
																				alignItems: "center",
																				boxShadow: "0 0 5px rgba(0,0,0,0.2)",
																				zIndex: 1,
																			}}
																			onClick={() => handleRemoveDocument(index)}
																		>
																			<span style={{ fontSize: "16px", fontWeight: "bold" }}>
																				&times;
																			</span>
																		</div>
																		{isImage && doc instanceof File ? (
																			<img
																				src={URL.createObjectURL(doc)}
																				alt={`New Document ${index + 1}`}
																				style={{
																					maxWidth: 100,
																					maxHeight: 100,
																					objectFit: "contain",
																				}}
																			/>
																		) : (
																			<FaiBox
																				display="flex"
																				flexDirection="column"
																				alignItems="center"
																				p={2}
																			>
																				<FaiTypography variant="h5">📄</FaiTypography>
																				<FaiTypography variant="caption" sx={{ wordBreak: "break-all" }}>
																					{fileName.length > 15 ? fileName.slice(0, 15) + "..." : fileName}
																				</FaiTypography>
																			</FaiBox>
																		)}
																	</FaiBox>
																);
															})}
														</FaiBox>
													</FaiBox>
												)}

												{/* Native file input for reliable multi-file upload */}
												<FaiBox
													component="label"
													display="flex"
													flexDirection="column"
													alignItems="center"
													justifyContent="center"
													p={3}
													border="2px dashed #ccc"
													borderRadius={2}
													sx={{
														cursor: "pointer",
														"&:hover": {
															borderColor: "#1976d2",
															backgroundColor: "rgba(25, 118, 210, 0.04)",
														},
													}}
												>
													<FaiTypography variant="h4" color="text.secondary">📁</FaiTypography>
													<FaiTypography variant="body2" color="text.secondary" mt={1}>
														Click to browse or drag files here
													</FaiTypography>
													<input
														type="file"
														multiple
														accept=".jpg,.jpeg,.png,.svg,.pdf,.doc,.docx,.xls,.xlsx,.txt"
														style={{ display: "none" }}
														onChange={(e) => {
															const files = e.target.files;
															if (files && files.length > 0) {
																const newFiles = Array.from(files);
																setCategoryImages((prev) => [...prev, ...newFiles]);
															}
															// Reset input value so same file can be selected again
															e.target.value = "";
														}}
													/>
												</FaiBox>
												<FaiTypography color="secondary" variant="caption">
													Supports: .jpg, .png, .svg, .pdf, .doc, .docx, .xls, .xlsx, .txt (Max 10 files)
												</FaiTypography>
											</FaiBox>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"GST Number"}
													name={"gstNumber"}
													placeholder={"Enter GST no."}
													value={values.gstNumber}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.gstNumber && Boolean(errors.gstNumber)}
													helperText={touched.gstNumber && errors.gstNumber}
												/>
											</FaiBox>
										</Grid>
									</Grid>

									<FaiBox pt={"15px"}>
										<Grid container justifyContent="flex-end" spacing={2}>
											<Grid item sm={2} xs={12}>
												<FaiButton
													color="primary"
													variant={"contained"}
													fullWidth
													type="submit"
													disabled={props.isSubmitting}
												>
													{props.isSubmitting
														? "Submitting..."
														: profile.businessInformation
															? "Submit"
															: "Next"}
												</FaiButton>
											</Grid>
										</Grid>
									</FaiBox>
								</Form>
							);
						}}
					</Formik>
				</FaiBox>
			</FaiBox>
		</>
	);
}
export default BusinessDetails;